import * as React from "react";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export interface ChartConfigItem {
    label: string;
    color: string;
}

export type ChartConfig = Record<string, ChartConfigItem>;

interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
    config: ChartConfig;
    children: React.ReactNode;
}

export function ChartContainer({
    config,
    children,
    className,
    ...props
}: ChartContainerProps) {
    const cssVariables = React.useMemo(() => {
        return Object.entries(config).reduce(
            (acc, [key, value]) => {
                acc[`--color-${key}`] = value.color;
                return acc;
            },
            {} as Record<string, string>
        );
    }, [config]);

    return (
        <div
            className={cn("h-[240px] w-full", className)}
            style={cssVariables}
            {...props}
        >
            {children}
        </div>
    );
}

interface ChartTooltipContentProps {
    active?: boolean;
    payload?: Array<{
        name: string;
        value: number;
        color: string;
        payload: {
            [key: string]: any;
        };
    }>;
    label?: string;
    indicator?: "dot" | "line";
}

export function ChartTooltipContent({
    active,
    payload,
    label,
    indicator = "dot",
}: ChartTooltipContentProps) {
    if (!active || !payload?.length) {
        return null;
    }

    return (
        <div className="rounded-lg border bg-background p-2 shadow-sm">
            <div className="grid gap-0.5">
                <p className="text-xs text-muted-foreground">{label}</p>
                {payload.map((item, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                        {indicator === "dot" && (
                            <div
                                className="h-1.5 w-1.5 rounded-full"
                                style={{ background: item.color }}
                            />
                        )}
                        {indicator === "line" && (
                            <div
                                className="h-0.5 w-2.5"
                                style={{ background: item.color }}
                            />
                        )}
                        <span className="text-xs font-medium">
                            {item.name}: {item.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function ChartTooltip(props: any) {
    return <props.content {...props} />;
}

// Helper function to combine class names
function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
} 